<template>
  <div class="wrapper">
    <div
      v-if="fetchLoading"
      class="height100 d-flex flex-column justify-center align-center text-center"
    >
      <div style="margin-bottom: 10px; color: #EE6090; font-size: 16px;">
        {{ t('home.loadingReleaseInfo') }}
      </div>
      <v-progress-circular indeterminate color="#EE6090" size="50"></v-progress-circular>
    </div>

    <div class="children-header d-flex" v-if="!fetchLoading">
      <div
        v-for="c in children"
        :key="c.childId"
        class="icon mr-2"
        @click="selectChild(c.childId)"
      >
        <img
          :class="{ active: c.childId === selectedChildId }"
          :src="resolveChildImageUrl(c.imageUrl) || 'https://via.placeholder.com/240x180?text=Sample+Image+1'"
        >
      </div>
      <v-btn
        @click="$router.push({ path: '/child/add', name: 'ChildAdd' })"
        color="primary"
        variant="outlined"
        icon="mdi-plus"
        size="60"
        class="custom-plus-btn"
      ></v-btn>
    </div>

    <div v-if="!fetchLoading">
      <div
        class="feed_wrapper no-scroll-bar"
        v-for="fByDate in feedsByDate"
        :key="Object.keys(fByDate)[0]"
        v-show="fByDate[Object.keys(fByDate)[0]].length > 0"
      >
        <div class="pa-1 today-wrapper">
          <h5>{{ formatFeedSectionDate(Object.keys(fByDate)[0]) }}</h5>
        </div>

        <v-list density="compact" lines="three" class="no-scroll-bar pa-0">
          <template
            v-for="f in fByDate[Object.keys(fByDate)[0]]"
            :key="f.feedId + f.body"
          >
            <v-list-item @click="clickFeed(f)" class="position-relative">
              <template v-slot:prepend>
                <v-avatar class="ml-2">
                  <v-img :src="resolveChildImageUrl(f.child?.imageUrl)" />
                </v-avatar>
              </template>

              <v-list-item-title
                class="font-weight-bold text-subtitle-1"
                style="white-space:pre-wrap;"
              >{{ formatFeedTitle(f) }}</v-list-item-title>

              <v-list-item-subtitle
                class="feed-subtitle text-subtitle-2 text-grey-darken-1"
                style="white-space:pre-wrap; max-height: 40px;"
              >{{ formatFeedBody(f.body) }}</v-list-item-subtitle>

              <template v-slot:append>
                <div class="d-flex flex-column align-end">
                  <span class="text-grey-darken-1 mb-1" style="font-size: 0.65rem;">
                    {{ formatMinutes(f.createdAt) }}
                  </span>
                  <div v-if="f.unReadCnt" class="badge d-flex justify-center align-center">
                    {{ f.unReadCnt }}
                  </div>
                </div>
              </template>
            </v-list-item>
            <v-divider />
          </template>
        </v-list>
      </div>
    </div>

    <div class="d-flex justify-center pa-5" v-if="!fetchLoading">
      <v-btn
        @click="fetchMoreFeed"
        color="primary"
        variant="text"
        :loading="isFetchMoreFeed"
      >
        {{ t('home.loadMore') }}
      </v-btn>
    </div>

    <div v-if="isNewInformation" class="speech-bubble-wrapper">
      <div class="speech-bubble speech-bubble-right-bottom">
        <strong>{{ t('home.newAnnouncement') }}</strong>
      </div>
    </div>

    <div class="fab-wrapper">
      <v-btn
        color="primary"
        icon
        size="large"
        elevation="4"
        @click="fab = !fab"
      >
        <v-icon>{{ fab ? 'mdi-close' : 'mdi-plus' }}</v-icon>
      </v-btn>

      <v-slide-y-reverse-transition>
        <v-card
          v-if="fab"
          v-click-outside="() => fab = false"
          class="fab-menu-card pa-0 mb-4" 
          variant="flat"
          elevation="0" 
          style="
            background-color: transparent !important;
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;
            box-shadow: none !important;
          "
        >
          <div class="d-flex flex-column ga-3">
            <v-btn
              v-if="SHOW_FAB_RELEASE_INFO"
              rounded="pill"
              size="large"
              :variant="isNewInformation ? 'flat' : 'outlined'"
              color="primary"
              class="align-self-end"
              :style="{
                  minWidth: '0',
                  width: 'fit-content',
                  // 未読がある（isNewInformation）ときは指定なし（Vuetifyのprimaryが適用）、
                  // そうでないときは白背景を適用
                  backgroundColor: isNewInformation ? '' : 'rgba(255, 255, 255, 1) !important',
                  // 文字色も同様に、未読時は白、それ以外は指定なし（outlinedのときはprimary色になる）
                  color: isNewInformation ? 'white !important' : ''
                }"
              @click="beforeOpenModal('information'); fab = false"
            >
              {{ t('home.fabReleaseInfo') }}
            </v-btn>

            <v-btn
              rounded="pill"
              size="large"
              variant="outlined"
              color="primary"
              class="align-self-end"
              style="
                min-width: 0;
                width: fit-content;
                background-color: rgba(255, 255, 255, 1) !important;
              "
              @click="beforeOpenModal('absence'); fab = false"
            >
              {{ t('home.fabAbsence') }}
            </v-btn>

            <v-btn
              rounded="pill"
              size="large"
              variant="outlined"
              color="primary"
              class="align-self-end"
              style="
                min-width: 0;
                width: fit-content;
                background-color: rgba(255, 255, 255, 1) !important;
              "
              @click="beforeOpenModal('pickup'); fab = false"
            >
              {{ t('home.fabPickup') }}
            </v-btn>
          </div>
        </v-card>
      </v-slide-y-reverse-transition>
    </div>

    <v-dialog v-model="isSelectChildDialog" width="90%" scroll-strategy="none">
      <v-card class="pa-2" rounded="lg" style="overflow: hidden; min-height: 100px;">
        <v-list density="compact">
          <v-list-item
            v-for="(child, i) in selectChildOpt"
            :key="i"
            @click="openModal(child.childId, child.lastName + child.firstName)"
            rounded="md"
          >
            <template v-slot:prepend>
              <v-avatar border>
                <v-img :src="resolveChildImageUrl(child.imageUrl) || 'https://via.placeholder.com/150'" />
              </v-avatar>
            </template>
            <v-list-item-title>{{ child.lastName + child.firstName }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isAttendanceDialog" width="300" scroll-strategy="none" attach="main">
      <v-card class="pa-4 text-center" rounded="xl">
        <v-img :src="qrCode ?? undefined" class="mb-4"></v-img>
        <p class="text-h5 mb-1">{{ selectedChildName }}</p>
        <p class="text-caption">{{ t('home.qrBarcodeHint') }}</p>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isAbsenceDialog" persistent width="500" scroll-strategy="none">
      <absence-modal
        :is-open="isAbsenceDialog"
        :child-id="selectedChildId"
        @posted="fetchFeed"
        @close="isAbsenceDialog = false"
      />
    </v-dialog>

    <v-dialog v-if="SHOW_INVOICE_UI" v-model="isInvoiceDialog" persistent width="500">
      <invoice-modal :is-open="isInvoiceDialog" @close="isInvoiceDialog = false" />
    </v-dialog>

    <v-dialog v-model="isPickupDialog" persistent width="500" scroll-strategy="none" attach="main">
      <pickup-modal
        :is-open="isPickupDialog"
        :child-id="selectedChildId"
        @posted="fetchFeed"
        @close="isPickupDialog = false"
      />
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import * as PusherPushNotifications from '@pusher/push-notifications-web';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import 'dayjs/locale/en';
import 'dayjs/locale/km';
import { t, dayjsLocale as appDayjsLocale } from '@/i18n';
dayjs.locale(appDayjsLocale);
import ApiVersioning from '@/api/ApiVersioning';
import { when } from '@/service/Switch';
import { formatHomeSectionDate, localizeDatesInText } from '@/utils/dateFormat';

import { meModule } from '@/store/entityModules/me';
import { attendanceModule } from '@/store/entityModules/attendance';
import { familyModule } from '@/store/entityModules/family';
import { invoiceModule } from '@/store/entityModules/invoice';
import { feedModule } from '@/store/entityModules/feed';
import { pusherModule } from '@/store/entityModules/pusher';
import { auth0Module } from '@/store/entityModules/auth0';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';

// components
import ChildrenHeader from '@/components/organisms/childrenHeader.vue';
import AbsenceModal from '@/components/modal/AbsenceModal.vue';
import InvoiceModal from '@/components/modal/InvoiceModal.vue';
import PickupModal from '@/components/modal/PickupModal.vue';

// model
import {
  FetchFeedsRequest,
  PostFeedReadRequest,
} from '@/model/Feed';
import { FeedResponse } from '@API/src/component/home/feed/types';
import LocalDataService from '../service/LocalDataServices';
import { SHOW_INVOICE_UI } from '@/constants/invoiceUi';
import { resolveChildImageUrl } from '@/utils/childImageUrl';

const SHOW_FAB_RELEASE_INFO = false;

enum ModalType {
  INFORMATION = 'information',
  ABSENCE = 'absence',
  PICKUP = 'pickup',
}

enum FeedType {
  ABSENT_CONFIRMED = 'absentConfirmed',
  PICKUP_CONFIRMED = 'pickupConfirmed',
  CHAT_RECEIVED = 'chatReceived',
  EVENT_CREATED = 'eventCreated',
  EVENT_UPDATED = 'eventUpdated',
  EVENT_DELETED = 'eventDeleted',
}

interface DataType {
  children: any[],
  selectedChildId: number;
  selectedChildName: string;
  fromDate: string;
  toDate: string;
  fab: boolean;
  isSelectChildDialog: boolean;
  modalType: ModalType;
  isAttendanceDialog: boolean;
  isAbsenceDialog: boolean;
  isInvoiceDialog: boolean;
  isPickupDialog: boolean;
  isFetchMoreFeed: boolean;
  isNewInformation: boolean;
  fetchLoading: boolean;
}

export default defineComponent({
  name: 'Home',

  setup() {
    return { SHOW_INVOICE_UI, SHOW_FAB_RELEASE_INFO };
  },

  components: {
    ChildrenHeader,
    AbsenceModal,
    InvoiceModal,
    PickupModal,
    Loading,
  },

  data: (): DataType => ({
    children: [],
    selectedChildId: NaN,
    selectedChildName: '',
    fromDate: dayjs().add(-100, 'd').format('YYYY-MM-DD'),
    toDate: dayjs().format('YYYY-MM-DD'),
    fab: false,
    isSelectChildDialog: false,
    modalType: ModalType.ABSENCE,
    isAttendanceDialog: false,
    isAbsenceDialog: false,
    isInvoiceDialog: false,
    isPickupDialog: false,
    isFetchMoreFeed: false,
    isNewInformation: false, 
    fetchLoading: false,
  }),

  computed: {
    async family() {
      return await familyModule.family || null;
    },
    selectChildOpt() {
      if (familyModule.family) {
        const children = familyModule.family.children;
        const opt = children.filter((c) => {
          let leaveDate: string | null = null;
          if (c.graduationDate && c.quitDate) {
            if (dayjs(c.graduationDate).isAfter(c.quitDate)) {
              leaveDate = c.quitDate;
            } else {
              leaveDate = c.graduationDate;
            }
          } else if (c.graduationDate) {
            leaveDate = c.graduationDate;
          } else if (c.quitDate) {
            leaveDate = c.quitDate;
          }
          const isQuit = !!leaveDate && dayjs(leaveDate).isBefore(dayjs());
          if (!isQuit) {
            return c;
          }
        });
        return opt;
      }
      return [];
    },
    feeds() {
      const feed = feedModule.feeds;
      if (this.selectedChildId) {
        return feed.filter((f) => {
          return f.child.childId === this.selectedChildId;
        });
      } else {
        return feedModule.feeds;
      }
    },
    feedsByDate() {
      // 1. 日付をキーにしたオブジェクトにまとめる O(N)
      const grouped = this.feeds.reduce((acc, f) => {
        if (!f.createdAt) return acc;

        const date = dayjs(f.createdAt).format('YYYY-MM-DD');
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(f);
        return acc;
      }, {} as Record<string, FeedResponse[]>);

      // 2. 直近100日分の配列を作成する
      return Array.from({ length: 100 }, (_, i) => {
        const date = dayjs().subtract(i, 'd').format('YYYY-MM-DD');
        return { [date]: grouped[date] || [] };
      });
    },
    today(): string {
      return formatHomeSectionDate(dayjs());
    },
    qrCode(): string {
      return attendanceModule.qrCode;
    },
    me() {
      return meModule.me;
    },
  },

  methods: {
    t,
    resolveChildImageUrl,
    formatFeedTitle(feed: FeedResponse): string {
      return when(feed.feedType)
        .on((v) => v === FeedType.ABSENT_CONFIRMED, () => t('settingNotification.notifications.absentConfirmed'))
        // fix show language follow setting
        .on((v) => v === FeedType.PICKUP_CONFIRMED, () => t('settingNotification.notifications.pickupConfirmedTitle'))
        .on((v) => v === FeedType.CHAT_RECEIVED, () => t('settingNotification.notifications.chatReceivedTitle'))
        .otherwise(() => feed.title);
    },
    formatFeedSectionDate(isoDate: string): string {
      return formatHomeSectionDate(isoDate);
    },
    formatFeedBody(body: string): string {
      return localizeDatesInText(body ?? '');
    },
    async getChildrenList() {
      const familyData = await this.family; // ここで await が必要！

      if (familyData) {
          // familyData は FamilyResponse | null の型になる
          const familyChildren: any[] = familyData.children; 
          console.log(familyChildren);
          return familyChildren;
      }
      return [];
    },

    async fetchFeed(
      childId: number | null = null,
    ) {
      this.isFetchMoreFeed = true;
      const response = await feedModule.fetchFeed({
        childId,
        fromDate: this.fromDate,
        toDate: this.toDate,
      });
      feedModule.setFeed(response.feeds);
      this.isFetchMoreFeed = false;
      const user = meModule.me
      if(user) {
            console.log(user.isNewinfo)
            this.isNewInformation = user.isNewinfo
      }
    },

    async fetchMoreFeed() {
      const currentFromDate = this.fromDate;
      this.toDate = dayjs(currentFromDate).add(-1, 'd').format('YYYY-MM-DD');
      this.fromDate = dayjs(this.toDate).add(-100, 'd').format('YYYY-MM-DD');
      this.isFetchMoreFeed = true;
      const response = await feedModule.fetchFeed({
        childId: null,
        fromDate: this.fromDate,
        toDate: this.toDate,
      });
      feedModule.addFeed(response.feeds);
      this.isFetchMoreFeed = false;
    },

    selectChild(childId: number) {
      if (this.selectedChildId === childId) {
        this.selectedChildId = NaN;
      } else {
        this.selectedChildId = childId;
      }
    },

    async beforeOpenModal(modalType: ModalType) {
      this.modalType = modalType;
      // this.isSelectChildDialog = true
      // return
      if (this.children.length === 1) {
        this.selectedChildId = this.children[0].childId;
        this.openModal();
      } else if (this.modalType === 'information') {
          this.fetchLoading = true;
          this.isNewInformation = false;
          await this.putMe()
          await this.fetchFeed()
          this.fetchLoading = false;
          this.externalLink()
      } else {
        this.isSelectChildDialog = true;
      }
    },

    openModal(childId: number | null = null, childName: string = '') {
      if (childId) {
        this.selectedChildId = childId;
        this.selectedChildName = childName;
      }
      when(this.modalType)
        .on((v) => v === 'information', async () => {
          this.fetchLoading = true;
          this.isNewInformation = false;
          await this.putMe()
          await this.fetchFeed()
          this.fetchLoading = false;
          this.externalLink()
        })
        .on((v) => v === 'absence', () => {
          this.isSelectChildDialog = false;
          this.isAbsenceDialog = true;
        })
        .on((v) => v === 'pickup', () => {
          this.isSelectChildDialog = false;
          this.isPickupDialog = true;
        });
    },

    externalLink() {
      const url = 'https://ccs-home-manual.super.site/%e3%83%aa%e3%83%aa%e3%83%bc%e3%82%b9%e6%83%85%e5%a0%b1'
      window.location.href = url
    },
    async putMe() {
      if (this.me) {
        try {
          await meModule.putMe({
            firstName: this.me.firstName,
            lastName: this.me.lastName,
            firstNameKana: this.me.firstNameKana,
            lastNameKana: this.me.lastNameKana,
            imageUrl: this.me.imageUrl,
            birth: this.me.birth,
            bloodType: this.me.bloodType,
            tel: this.me.tel,
            notifications: [],
            isNewinfo: this.isNewInformation,
          });
        } catch {}
      }
    },

    clickFeed(feed: FeedResponse) {
      feedModule.postFeed({ feedId: feed.feedId });
      when(feed.feedType)
        .on((v) => v === FeedType.ABSENT_CONFIRMED || v === FeedType.PICKUP_CONFIRMED || v === FeedType.CHAT_RECEIVED, () => {
          const date = feed.date;
          const createdAt = feed.createdAt;
          this.$router.push({ name: 'Chat', query: {
            childId: String(feed.child.childId),
            childName: feed.child.lastName + feed.child.firstName,
            date: date? date : createdAt,
          }});
        })
        .on((v) => v === FeedType.EVENT_CREATED || v === FeedType.EVENT_UPDATED, () => {
          this.$router.push({ query: {
            childId: String(feed.child.childId),
            childName: feed.child.lastName + feed.child.firstName,
          }});
        });
    },

    formatMinutes(time: string): string {
      return dayjs(time).format('HH:mm');
    },

    async initBeams() {
      try {
        const sessionAndToken = LocalDataService.getBeamsTokenAndId();
        if ((window as any).webkit && (window as any).webkit.messageHandlers !== undefined) {
          (window as any).webkit.messageHandlers.pusher.postMessage(`${sessionAndToken}`);
        }
        /*  Pusher */
        const url = process.env.VUE_APP_API_URL;
        const instanceId = process.env.VUE_APP_BEAMS_INSTANCE_ID;
        const apiKey = process.env.VUE_APP_X_API_KEY;
        const accessToken = await auth0Module.getAuth0Token();
        const user = meModule.me

        if (instanceId && apiKey && user) {
          const currentUserId = `${user.parentId}`;

          const beamsTokenProvider = new PusherPushNotifications.TokenProvider({
            url: url + '/push/auth',
            queryParams: {
              user_id: currentUserId,
            },
            headers: {
              'Authorization' : `Bearer ${accessToken}`,
              'Content-Type': 'application/json; charset=utf-8',
              'x-api-key': apiKey,
            },
          });
          window.navigator.serviceWorker.ready.then(async (serviceWorkerRegistration) => {
            const beamsClient = new PusherPushNotifications.Client({
              instanceId,
              serviceWorkerRegistration,
            });
            beamsClient
              .start()
              .then(() => beamsClient.getDeviceId())
              .then((deviceId) => {
                console.log('Device ID:', deviceId);
              })
              .then(() => beamsClient.getDeviceInterests())
              .then(async (interests) => {
                await Promise.all(
                  interests.map((interest) => {
                    // 同じ端末で違うアカウントでログインしていた場合、そのsubscribeが残っているため、解除する
                    if (interest !== `FAM-${currentUserId}` && interest !== `debug-FAM-${currentUserId}`) {
                      beamsClient.removeDeviceInterest(interest);
                    }
                  }),
                );
                if (!interests.includes(`FAM-${currentUserId}`)) {
                  await beamsClient.addDeviceInterest(`FAM-${currentUserId}`);
                }
                if (!interests.includes(`debug-FAM-${currentUserId}`)) {
                  // debug console用のsubscribe
                  await beamsClient.addDeviceInterest(`debug-FAM-${currentUserId}`);
                }
              })
              .then(() => beamsClient.getDeviceInterests())
              .then((interest) => {
                console.log('PUSHER BEAMS Interest:', interest);
              })
              .then(() => beamsClient.setUserId(currentUserId, beamsTokenProvider))
              .then(() => beamsClient.getUserId())
              .then((userId) => {
                console.log('PUSHER BEAMS userId:', userId);
              })
              .catch((error) => {
                console.log('error', error);
              });
            });

        }
      } catch (e) {}
    },
  },

  async mounted() {
    if (!familyModule.family && meModule.me) {
      await familyModule.fetchFamilyById({
        familyId: meModule.me.families[0].familyId,
      });
    }
    let isInvoiceDialog = false;
    if (await this.family) {
      const familyChildren: any[] = await this.getChildrenList();
      if (!SHOW_INVOICE_UI) {
        this.children = familyChildren.map((child) => ({
          ...child,
          noneRead: false,
        }));
      } else {
        const results = await Promise.all(
          familyChildren.map(async (child) => {
            const childId = Number(child.childId);

            await invoiceModule.fetchInvoices({ childId, year: null });

            const invoices = invoiceModule.invoices || [];

            const noneRead = invoices.some((inv) => !inv.readDate);

            if (noneRead) {
              isInvoiceDialog = true;
            }

            return {
              ...child,
              noneRead,
            };
          }),
        );

        this.children = results;
      }
    }
    this.isInvoiceDialog = isInvoiceDialog;
    //ApiVersioning.checkVersion();
    this.fetchFeed();
    this.initBeams();
  },
});
</script>
<style lang="scss" scoped>
.wrapper {
  padding: 76px 0 56px;
  position: relative;

  .children-header {
    width: 100vw;
    position: fixed;
    left: 0;
    top: calc(env(safe-area-inset-top));
    background-color: #ffffff;
    z-index: 3;
    padding: 8px;
    overflow-x: auto;
    border-bottom: 1px solid #eee;

    .icon {
      height: 60px;
      min-width: 60px;
      border-radius: 50%;
      img {
        height: 60px;
        width: 60px;
        border-radius: 50%;
        object-fit: cover;
        border: 2px solid transparent;
        &.active {
          border-color: rgb(var(--v-theme-primary));
        }
      }
    }
  }

  .today-wrapper {
    color: #A3A4A4;
    background-color: #F5F6F6;
    font-size: 0.8rem;
  }

  .badge {
    height: 20px;
    min-width: 20px;
    padding: 0 4px;
    border-radius: 10px;
    font-size: 0.7rem;
    color: #ffffff;
    background-color: rgb(var(--v-theme-primary));
  }

  /* FABと縦並びメニューのスタイル */
  .fab-wrapper {
    position: fixed;
    bottom: 80px;
    right: 16px;
    display: flex;
    flex-direction: column-reverse;
    align-items: flex-end;
    z-index: 500 !important;
  }

  .fab-menu-card {
    width: 200px;
    background-color: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(4px);
  }

  /* 吹き出し */
  .speech-bubble-wrapper {
    position: fixed;
    bottom: 100px;
    right: 80px;
    z-index: 99;
  }
  .speech-bubble {
    position: relative;
    background: #EE6090;
    border-radius: .5em;
    padding: 10px 15px;
    color: white;
    font-size: 0.85rem;
    &:after {
      content: '';
      position: absolute;
      right: -8px;
      bottom: 15px;
      border: 8px solid transparent;
      border-left-color: #EE6090;
    }
  }

  .custom-plus-btn {
    border: 2px solid rgb(var(--v-theme-primary)) !important;
  }
}
</style>
